import React, { useState, useEffect } from 'react';
import { Link, NavLink, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCustomerAuth } from '../../../contexts/CustomerAuthContext';
import customerApi from '../../../services/customerApi';
import contactApi from '../../../services/contactApi.js';
import { useSiteBrand } from '../../../contexts/SiteBrandContext';
import { subscriptionPlanApi } from '../../../services/subscriberApi';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';

const REASONS = [
  'General Inquiry',
  'Subscription Issue',
  'Delivery Problem',
  'Digital Download Help',
  'Refund Request',
  'Business / Advertise',
  'Press & Media',
  'Other',
];

const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>
);

const fmtPKR = n => 'Rs ' + Number(n || 0).toLocaleString('en-PK');
const fmtDate = iso => {
  try { return new Date(iso).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};
const fmtDT = iso => {
  try { return new Date(iso).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

function useCustomerAccount() {
  const { user } = useCustomerAuth();
  const [data, setData] = useState({ orders: [], subscriptions: [], invoices: [], loading: true });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: body } = await customerApi.account();
        if (cancelled) return;
        setData({
          orders: Array.isArray(body.orders) ? body.orders : [],
          subscriptions: Array.isArray(body.subscriptions) ? body.subscriptions : [],
          invoices: Array.isArray(body.invoices) ? body.invoices : [],
          loading: false,
        });
      } catch {
        if (!cancelled) setData((d) => ({ ...d, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);
  return data;
}

function printInvoice(inv) {
  const items = (inv.items || []).map((it) => `${it.qty || 1} × ${it.title || 'Flare Magazine'}`).join('<br/>');
  const w = window.open('', '_blank', 'width=720,height=900');
  if (!w) {
    toast.error('Allow pop-ups to print the invoice.');
    return;
  }
  w.document.write(`<!doctype html><html><head><title>${inv.invoice_number}</title>
    <style>body{font-family:Georgia,serif;padding:32px;color:#111}h1{color:#c4151c;margin:0 0 8px}table{width:100%;border-collapse:collapse;margin-top:24px}td{padding:6px 0}</style>
    </head><body>
    <h1>Flare Magazine</h1>
    <div>Invoice ${inv.invoice_number}</div>
    <table>
      <tr><td>Order</td><td>${inv.order_number || '—'}</td></tr>
      <tr><td>Date</td><td>${fmtDate(inv.created_at)}</td></tr>
      <tr><td>Amount</td><td>${fmtPKR(inv.grand_total)}</td></tr>
      <tr><td>Method</td><td>${inv.method || '—'}</td></tr>
      <tr><td>Status</td><td>${inv.status || 'paid'}</td></tr>
      <tr><td>Items</td><td>${items || '—'}</td></tr>
    </table>
    <p style="margin-top:32px;font-size:12px;color:#666">Thank you for supporting Flare Magazine.</p>
    </body></html>`);
  w.document.close();
  w.focus();
  w.print();
}

function ProtectedCustomerRoute({ children }) {
  const { user, loading, logout } = useCustomerAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    if (!user) {
      const current = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/login?redirect=${current}`, { replace: true });
      return;
    }
    if (user.status === 'banned' || user.banned) {
      (async () => {
        try { await logout(); } catch {}
        toast.error('Your account has been restricted. Please contact support.');
        navigate('/login', { replace: true });
      })();
    }
  }, [user, loading, navigate, logout]);
  if (loading) return null;
  if (!user) return null;
  return children;
}

const CUSTOMER_LINKS = [
  { to: '/account', iconPath: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', text: 'Dashboard', end: true },
  { to: '/account/subscriptions', iconPath: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z', text: 'Subscriptions' },
  { to: '/account/orders', iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', text: 'My Orders' },
  { to: '/account/invoices', iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', text: 'Invoices' },
  { to: '/account/addresses', iconPath: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z', text: 'Addresses' },
  { to: '/account/preferences', iconPath: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', text: 'Preferences' },
  { to: '/account/profile', iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', text: 'Profile' },
  { to: '/account/security', iconPath: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', text: 'Security' },
];

function SidebarLink({ to, iconPath, text, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) =>
      `w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
        isActive ? 'text-white shadow-md' : 'text-gray-300 hover:text-white hover:bg-white/10'
      }`} style={({ isActive }) => isActive ? { background: `linear-gradient(135deg, ${FLARE_RED} 0%, ${FLARE_RED_DARK} 100%)` } : {}}
    >
      <Icon path={iconPath} className="h-5 w-5 shrink-0" />
      <span>{text}</span>
    </NavLink>
  );
}

function CustomerAccountLayout() {
  const { user, logout } = useCustomerAuth();
  const brand = useSiteBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('You have been logged out');
    navigate('/', { replace: true });
  };

  const content = (
    <div className="flex min-h-screen" style={{ background: 'linear-gradient(135deg,#fff1f2 0%,#ffffff 40%,#fff7ed 100%)' }}>
      <aside className={'fixed inset-y-0 left-0 z-40 w-72 transform transition-transform duration-200 lg:relative lg:translate-x-0 ' + (mobileOpen ? 'translate-x-0' : '-translate-x-full')}
        style={{ background: `linear-gradient(180deg,#111827 0%,#1f2937 100%)` }}
      >
        <div className="flex h-full flex-col p-5 gap-2">
          <Link to="/" className="mb-4 flex items-center justify-center px-2">
          <div className="flex flex-col items-center justify-center rounded-xl px-4 py-3 bg-white border border-gray-100 transition-shadow"
               style={{ boxShadow: '0 2px 14px -3px rgba(196,21,28,0.3)', width: '88%' }}>
            <img src={brand.userLogo} alt="Flare" className="h-auto drop-shadow-sm"
                 style={{ width: '95%', maxWidth: '100%', objectFit: 'contain' }}
                 onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/Flare Logo.png'; }} />
            <div className="w-full text-[11px] text-gray-500 uppercase tracking-[0.22em] font-bold leading-none mt-0 text-center">
              {brand.domain}
            </div>
          </div>
        </Link>

          <div className="rounded-2xl p-4 mb-2" style={{ background: 'linear-gradient(135deg, rgba(196,21,28,0.22) 0%, rgba(196,21,28,0.08) 100%)', border: '1px solid rgba(196,21,28,0.4)' }}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-white text-lg font-black shadow-sm" style={{ backgroundColor: FLARE_RED }}>
                {String(user?.name || 'C').trim().charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="text-white font-extrabold text-base truncate">{user?.name || 'Customer'}</div>
                <div className="flex items-center text-gray-300 text-xs font-medium truncate">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1.5 opacity-90 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  <span className="truncate">{user?.email}</span>
                </div>
                {user?.phone ? <div className="flex items-center text-gray-400 text-[11px] font-medium truncate">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  <span className="truncate">{user.phone}</span>
                </div> : null}
              </div>
            </div>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto pr-1">
            {CUSTOMER_LINKS.map(l => (
              <SidebarLink key={l.to} {...l} />
            ))}
          </nav>

          <div className="pt-4 mt-4 border-t border-gray-700/50 space-y-1">
            <Link to="/plans" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-300 hover:text-white hover:bg-white/10 transition-all">
              <Icon path="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              Upgrade / Change Plan
            </Link>
            <Link to="/account/support" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-300 hover:text-white hover:bg-white/10 transition-all">
              <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              Contact Support
            </Link>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-300 hover:text-white hover:bg-red-500/20 transition-all">
              <Icon path="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              Log out
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileOpen(true)} className="lg:hidden inline-flex w-10 h-10 items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
                <Icon path="M4 6h16M4 12h16M4 18h16" />
              </button>
              <div className="hidden sm:block">
                <Link to="/" className="text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">
                  ← Back to {brand.domain}
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/cart" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50">
                <Icon path="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" className="h-4 w-4" />
                Cart
              </Link>
              <div className="hidden sm:flex items-center gap-2 pl-3 ml-1 border-l border-gray-200">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-black shadow-sm" style={{ backgroundColor: FLARE_RED }}>
                  {String(user?.name || 'C').trim().charAt(0).toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Routes location={location} key={location.pathname}>
            <Route index element={<CustomerDashboard />} />
            <Route path="subscriptions" element={<CustomerSubscriptions />} />
            <Route path="orders" element={<CustomerOrders />} />
            <Route path="invoices" element={<CustomerInvoices />} />
            <Route path="addresses" element={<CustomerAddresses />} />
            <Route path="preferences" element={<CustomerPreferences />} />
            <Route path="profile" element={<CustomerProfile />} />
            <Route path="security" element={<CustomerSecurity />} />
            <Route path="support" element={<CustomerSupport />} />
            <Route path="*" element={<Navigate to="/account" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );

  return content;
}

function StatCard({ title, value, sub, accent = '#c4151c', iconPath }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">{title}</div>
          <div className="text-2xl font-black text-gray-900 break-words">{value}</div>
          {sub ? <div className="text-xs font-semibold text-gray-500 mt-1">{sub}</div> : null}
        </div>
        <div className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ backgroundColor: accent }}>
          <Icon path={iconPath} className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function CustomerDashboard() {
  const { user, refresh } = useCustomerAuth();
  const { orders, subscriptions: subs, invoices: inv } = useCustomerAccount();
  const [stats, setStats] = useState({ subs: 0, orders: 0, invoices: 0, saved: 0 });
  const [nextDelivery, setNextDelivery] = useState(null);

  useEffect(() => {
    refresh();
    let saved = 0;
    subs.forEach(sub => {
      const months = Number(sub.duration_months) || 1;
      const baseline = months * 1500;
      saved += Math.max(0, baseline - Number(sub.price_pkr || 0));
    });
    setStats({ subs: subs.length, orders: orders.length, invoices: inv.length, saved });

    let nd = null;
    let ndTime = Infinity;
    subs.forEach(sub => {
      const issues = Array.isArray(sub.issues) ? sub.issues : [];
      const next = issues.find(i => !i.delivered && !i.in_transit);
      if (next) {
        const t = new Date(next.scheduled_date).getTime();
        if (!nd || t < ndTime) { nd = next; ndTime = t; }
      }
    });
    setNextDelivery(nd);
  }, [user.id, refresh, subs, orders, inv]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden shadow-md"
        style={{ background: `linear-gradient(135deg, ${FLARE_RED} 0%, ${FLARE_RED_DARK} 100%)` }}
      >
        <div className="absolute -top-20 -right-16 w-72 h-72 bg-white/10 rounded-full" />
        <div className="absolute -bottom-24 right-24 w-48 h-48 border-4 border-white/10 rounded-full" />
        <div className="relative">
          <div className="text-xs font-bold uppercase tracking-widest opacity-80 mb-2">Welcome back, subscriber</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">Hello, {user?.name || 'Friend'} 👋</h1>
          <p className="opacity-90 max-w-2xl">Manage your subscriptions, track deliveries, and get exclusive Flare member discounts in one secure place.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Active Plans" value={stats.subs} sub={subs[0]?.plan_name || 'No plan yet'} accent="#c4151c" iconPath="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        <StatCard title="Total Orders" value={stats.orders} sub={orders[0]?.order_number || 'Place first order'} accent="#4f46e5" iconPath="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        <StatCard title="Total Saved" value={fmtPKR(stats.saved)} sub="Vs pay-monthly (Rs 1500/issue)" accent="#059669" iconPath="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        <StatCard title="Invoices Paid" value={stats.invoices} sub="All records emailed to you" accent="#b45309" iconPath="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight">Your Subscriptions</h2>
              <p className="text-sm text-gray-500">Upcoming issues, delivery tracking & status.</p>
            </div>
            <Link to="/account/subscriptions" className="text-sm font-bold px-4 py-2 rounded-xl" style={{ color: FLARE_RED, backgroundColor: 'rgba(196,21,28,0.08)' }}>
              View all →
            </Link>
          </div>
          {subs.length === 0 ? (
            <div className="text-center py-16 rounded-2xl" style={{ background: 'linear-gradient(135deg,#fff7ed 0%,#ffffff 100%)' }}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fff1f2', color: FLARE_RED }}>
                <Icon path="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">No active subscriptions yet</h3>
              <p className="text-sm text-gray-500 mb-5">Subscribe to save 15–33% vs buying single copies.</p>
              <Link to="/plans" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-md hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: FLARE_RED }}>
                Browse Plans →
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {subs.slice(0, 2).map(sub => {
                const issues = Array.isArray(sub.issues) ? sub.issues : [];
                const delivered = issues.filter(i => i.delivered).length;
                const total = Number(sub.total_issues) || issues.length || 1;
                const pct = Math.round((delivered / total) * 100);
                return (
                  <div key={sub.id} className="rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider" style={{ backgroundColor: '#ecfdf5', color: '#059669' }}>
                            {sub.status === 'active' ? 'Active' : sub.status}
                          </span>
                          <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">{sub.duration_months} months · {sub.total_issues} issues</span>
                        </div>
                        <h3 className="text-lg font-black text-gray-900 tracking-tight">{sub.plan_name}</h3>
                        <div className="text-sm text-gray-500 mt-1">
                          <span className="font-bold text-gray-900">{fmtPKR(sub.price_pkr)}</span> · Avg {fmtPKR(sub.per_month_avg)} / month
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-black text-gray-900">{delivered}<span className="text-gray-400 text-lg font-semibold">/{total}</span></div>
                        <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Issues delivered</div>
                      </div>
                    </div>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-1">
                      <div className="h-full rounded-full" style={{ width: pct + '%', background: `linear-gradient(90deg, ${FLARE_RED} 0%, #ef4444 100%)` }} />
                    </div>
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-500 pt-1">
                      <span>Start {fmtDate(sub.start_date)}</span>
                      <span>{pct}% complete</span>
                      <span>End {fmtDate(sub.end_date)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-black text-gray-900 tracking-tight">Next Delivery</h2>
              <Icon path="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" className="h-5 w-5 text-gray-400" />
            </div>
            {nextDelivery ? (
              <div className="rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${FLARE_RED} 0%, #ea580c 100%)` }}>
                <div className="text-xs font-bold uppercase tracking-widest opacity-90 mb-1">Scheduled</div>
                <div className="text-2xl font-black mb-1">{nextDelivery.issue_title}</div>
                <div className="text-sm opacity-90 mb-4">Delivering on {fmtDate(nextDelivery.scheduled_date)}</div>
                <div className="flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2 text-xs font-bold">
                  <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" className="h-4 w-4" />
                  Tracking link will go live once shipped.
                </div>
              </div>
            ) : subs.length > 0 ? (
              <div className="rounded-2xl p-5" style={{ backgroundColor: '#ecfdf5' }}>
                <div className="text-[#059669] text-xs font-black uppercase tracking-widest mb-2">All caught up</div>
                <div className="text-sm font-bold text-gray-900">No upcoming issues scheduled.</div>
                <div className="text-xs text-gray-600 mt-1">Your plan is complete. Renew to keep the magazines coming.</div>
              </div>
            ) : (
              <div className="rounded-2xl p-5 text-center" style={{ backgroundColor: '#f3f4f6' }}>
                <div className="text-sm font-bold text-gray-600">No deliveries scheduled.</div>
                <Link to="/plans" className="block mt-2 text-xs font-black" style={{ color: FLARE_RED }}>Choose a plan →</Link>
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-lg font-black text-gray-900 tracking-tight mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <Link to="/account/subscriptions" className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: FLARE_RED }}><Icon path="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0" /></div><div><div className="text-sm font-bold text-gray-900">Manage Subscription</div><div className="text-xs text-gray-500">Pause, skip, renew</div></div></div>
                <Icon path="M9 5l7 7-7 7" className="h-4 w-4 text-gray-400" />
              </Link>
              <Link to="/account/addresses" className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: '#4f46e5' }}><Icon path="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" /></div><div><div className="text-sm font-bold text-gray-900">Update Address</div><div className="text-xs text-gray-500">Delivery locations</div></div></div>
                <Icon path="M9 5l7 7-7 7" className="h-4 w-4 text-gray-400" />
              </Link>
              <Link to="/account/invoices" className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: '#059669' }}><Icon path="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></div><div><div className="text-sm font-bold text-gray-900">Download Invoices</div><div className="text-xs text-gray-500">Tax receipts PDFs</div></div></div>
                <Icon path="M9 5l7 7-7 7" className="h-4 w-4 text-gray-400" />
              </Link>
              <Link to="/plans" className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: '#b45309' }}><Icon path="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></div><div><div className="text-sm font-bold text-gray-900">Upgrade Plan</div><div className="text-xs text-gray-500">Save more, pay less</div></div></div>
                <Icon path="M9 5l7 7-7 7" className="h-4 w-4 text-gray-400" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomerSubscriptions() {
  const { user } = useCustomerAuth();
  const { subscriptions: subs } = useCustomerAccount();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    subscriptionPlanApi.getAll().then(r => setPlans(r?.data?.data || [])).catch(() => {});
  }, [user.id]);

  const action = (msg) => toast.info(msg);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Membership</div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">My Subscriptions</h1>
          <p className="text-sm text-gray-500 mt-1">Pause, skip, renew, or upgrade anytime. No hidden fees.</p>
        </div>
        <Link to="/plans" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-md hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: FLARE_RED }}>
          <Icon path="M12 6v6m0 0v6m0-6h6m-6 0H6" className="h-5 w-5" />
          Add Another Plan
        </Link>
      </div>

      {subs.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-10 text-center">
          <div className="w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-4" style={{ backgroundColor: '#fff1f2' }}>
            <Icon path="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" className="h-10 w-10" style={{ color: FLARE_RED }} />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-2">No subscriptions yet</h2>
          <p className="text-gray-500 max-w-md mx-auto mb-6">Subscribe once and get a new Flare delivered to your door every month. Save up to 40% vs single copies.</p>
          <Link to="/plans" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold shadow-md" style={{ backgroundColor: FLARE_RED }}>Browse Subscription Plans →</Link>
        </div>
      ) : (
        <div className="space-y-6">
          {subs.map(sub => {
            const issues = Array.isArray(sub.issues) ? sub.issues : [];
            const delivered = issues.filter(i => i.delivered).length;
            const inTransit = issues.filter(i => i.in_transit).length;
            const pending = issues.filter(i => !i.delivered && !i.in_transit).length;
            const total = issues.length || 1;
            const pct = Math.round((delivered / total) * 100);
            return (
              <div key={sub.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 sm:p-8 border-b border-gray-100" style={{ background: 'linear-gradient(135deg,#fff 0%,#fff5f5 100%)' }}>
                  <div className="flex flex-wrap items-start justify-between gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider" style={{ backgroundColor: '#ecfdf5', color: '#059669' }}>● {String(sub.status).toUpperCase()}</span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider" style={{ backgroundColor: '#eef2ff', color: '#4f46e5' }}>{sub.duration_months} MONTH PLAN</span>
                        {sub.auto_renew ? <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>AUTO-RENEW: ON</span> : null}
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mb-2">{sub.plan_name}</h2>
                      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
                        <span className="font-black text-2xl text-gray-900">{fmtPKR(sub.price_pkr)}</span>
                        <span className="text-gray-500">paid total · Avg <span className="font-bold text-gray-900">{fmtPKR(sub.per_month_avg)}</span> / month</span>
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs font-semibold text-gray-500">
                        <span>📅 Started {fmtDate(sub.start_date)}</span>
                        <span>🏁 Ends {fmtDate(sub.end_date)}</span>
                        <span>🆔 {sub.id}</span>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="text-center min-w-[110px] p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                        <div className="text-3xl font-black">{delivered}/{total}</div>
                        <div className="text-[11px] font-black uppercase tracking-widest text-gray-500 mt-1">Delivered</div>
                      </div>
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        <button onClick={() => action('Email Flare support to pause a month.')} className="px-4 py-2.5 rounded-xl text-sm font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">⏸ Pause (1 mo)</button>
                        <button onClick={() => action('Email Flare support to skip the next issue.')} className="px-4 py-2.5 rounded-xl text-sm font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">⏭ Skip Next</button>
                        <button onClick={() => action('Email Flare support to cancel. Undispatched issues can be reviewed.')} className="px-4 py-2.5 rounded-xl text-sm font-bold text-red-700 bg-red-50 hover:bg-red-100 transition-colors">Cancel Plan</button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="w-full h-4 bg-white rounded-full border border-gray-200 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: pct + '%', background: `linear-gradient(90deg, ${FLARE_RED}, #ef4444)` }} />
                    </div>
                    <div className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-gray-500 mt-2">
                      <span>✅ {delivered} Delivered · 🚚 {inTransit} In Transit · ⏳ {pending} Scheduled</span>
                      <span>{pct}% Complete</span>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 p-6 sm:p-8">
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-widest text-gray-500 mb-4">📬 Delivery Address</h3>
                    <div className="rounded-2xl border border-gray-100 p-5 bg-gray-50/50">
                      <div className="font-bold text-gray-900">{sub.delivery_address?.name || user?.name}</div>
                      <div className="text-sm text-gray-600">{sub.delivery_address?.address_line1 || 'Address on file'}</div>
                      <div className="text-sm text-gray-600">{sub.delivery_address?.city || 'Lahore'}, Pakistan</div>
                      <div className="text-sm text-gray-500 mt-2">📞 {sub.delivery_address?.phone || user?.phone || 'Not set'}</div>
                      <Link to="/account/addresses" className="mt-4 inline-flex items-center gap-1.5 text-xs font-black" style={{ color: FLARE_RED }}>✏️ Change delivery details →</Link>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-widest text-gray-500 mb-4">💾 Issue Tracker</h3>
                    <div className="max-h-80 overflow-y-auto pr-1 space-y-2">
                      {issues.map((i, idx) => (
                        <div key={i.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50/70 transition-colors">
                          <div className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center text-xs font-black"
                            style={{
                              backgroundColor: i.delivered ? '#ecfdf5' : i.in_transit ? '#fff7ed' : '#f3f4f6',
                              color: i.delivered ? '#059669' : i.in_transit ? '#ea580c' : '#4b5563',
                            }}
                          >#{idx + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-gray-900 truncate">{i.issue_title}</div>
                            <div className="text-xs text-gray-500">Scheduled {fmtDate(i.scheduled_date)}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              {i.delivered ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: '#ecfdf5', color: '#059669' }}>
                                  ✔ Delivered {i.tracking_number ? `· ${i.tracking_number}` : ''}
                                </span>
                              ) : i.in_transit ? (
                                <>
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: '#fff7ed', color: '#ea580c' }}>🚚 In Transit · {i.tracking_number}</span>
                                  {i.tracking_url ? <a href={i.tracking_url} target="_blank" rel="noreferrer" className="text-[11px] font-bold" style={{ color: FLARE_RED }}>Track →</a> : null}
                                </>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: '#e5e7eb', color: '#4b5563' }}>⏳ Scheduled</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CustomerOrders() {
  const { orders } = useCustomerAccount();
  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Purchases</div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">My Orders</h1>
        <p className="text-sm text-gray-500 mt-1">All single-issue and subscription purchases — one place.</p>
      </div>
      {orders.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-10 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#f3f4f6' }}>
            <Icon path="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" className="h-8 w-8 text-gray-500" />
          </div>
          <h2 className="text-xl font-black text-gray-900 mb-2">No orders yet</h2>
          <p className="text-gray-500 mb-6">Once you order a magazine or subscribe, it will appear here.</p>
          <Link to="/magazines" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-md" style={{ backgroundColor: FLARE_RED }}>Browse Magazines →</Link>
        </div>
      ) : (
        <div className="overflow-hidden bg-white rounded-3xl border border-gray-100 shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest text-gray-500">Order</th>
                  <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest text-gray-500">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest text-gray-500">Items</th>
                  <th className="px-6 py-4 text-right text-xs font-black uppercase tracking-widest text-gray-500">Total</th>
                  <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest text-gray-500">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-black uppercase tracking-widest text-gray-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orders.map(o => (
                  <tr key={o.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-5">
                      <div className="font-black text-gray-900">{o.order_number}</div>
                      <div className="text-xs text-gray-500 mt-0.5">💳 {o.payment_method} · {o.payment_status}</div>
                    </td>
                    <td className="px-6 py-5 text-sm text-gray-600">{fmtDT(o.created_at)}</td>
                    <td className="px-6 py-5">
                      <ul className="space-y-0.5">
                        {(o.items || []).map((it, i) => <li key={i} className="text-sm text-gray-800"><span className="text-xs font-bold text-gray-400 mr-1">×{it.qty}</span>{it.title}</li>)}
                      </ul>
                    </td>
                    <td className="px-6 py-5 text-right">
                      <div className="font-black text-gray-900">{fmtPKR(o.grand_total)}</div>
                    </td>
                    <td className="px-6 py-5">
                      <span className={'inline-flex items-center px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider ' + (
                        o.status === 'delivered' ? 'bg-emerald-50 text-emerald-700'
                          : o.status === 'shipped' ? 'bg-orange-50 text-orange-700'
                            : 'bg-gray-100 text-gray-700'
                      )}>
                        {o.status}
                      </span>
                      {o.tracking_number ? (
                        <div className="mt-1.5">
                          <div className="font-mono text-[11px] font-bold text-gray-800">{o.tracking_number}</div>
                          {o.courier_status ? <div className="text-[11px] text-gray-500 mt-0.5">{o.courier_status}</div> : null}
                          {o.tracking_url ? (
                            <a href={o.tracking_url} target="_blank" rel="noreferrer" className="inline-block text-[11px] font-bold mt-0.5" style={{ color: FLARE_RED }}>
                              Track parcel →
                            </a>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-6 py-5 text-right space-x-2 whitespace-nowrap">
                      <Link to={`/order/${o.order_number}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white shadow-sm" style={{ backgroundColor: FLARE_RED }}>View</Link>
                      <button onClick={() => printInvoice({
                        invoice_number: 'INV-' + o.order_number,
                        order_number: o.order_number,
                        grand_total: o.grand_total,
                        method: o.payment_method,
                        status: o.payment_status,
                        created_at: o.created_at,
                        items: o.items,
                      })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-700 hover:bg-gray-50">Invoice</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerInvoices() {
  const { invoices: inv } = useCustomerAccount();
  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Payments</div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Invoices</h1>
        <p className="text-sm text-gray-500 mt-1">Download GST-compliant tax receipts for every payment.</p>
      </div>
      {inv.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-10 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
            <Icon path="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" className="h-8 w-8" style={{ color: '#b45309' }} />
          </div>
          <h2 className="text-xl font-black text-gray-900 mb-2">No invoices yet</h2>
          <p className="text-gray-500">Once payment is made, invoices appear here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest text-gray-500">Invoice #</th>
                  <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest text-gray-500">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest text-gray-500">Order</th>
                  <th className="px-6 py-4 text-right text-xs font-black uppercase tracking-widest text-gray-500">Amount</th>
                  <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest text-gray-500">Method</th>
                  <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest text-gray-500">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-black uppercase tracking-widest text-gray-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {inv.map(v => (
                  <tr key={v.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-5 font-black text-gray-900">{v.invoice_number}</td>
                    <td className="px-6 py-5 text-sm text-gray-600">{fmtDate(v.created_at)}</td>
                    <td className="px-6 py-5 text-sm text-gray-600">{v.order_number || '—'}</td>
                    <td className="px-6 py-5 text-right font-black text-gray-900">{fmtPKR(v.grand_total)}</td>
                    <td className="px-6 py-5 text-sm text-gray-600">💳 {v.method}</td>
                    <td className="px-6 py-5">
                      <span className={'inline-flex items-center px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider ' + (v.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>{v.status}</span>
                    </td>
                    <td className="px-6 py-5 text-right whitespace-nowrap space-x-2">
                      <button onClick={() => printInvoice(v)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold text-white shadow-sm" style={{ backgroundColor: FLARE_RED }}>⬇ Print / PDF</button>
                      {v.status !== 'paid' ? <button className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold border border-gray-200 text-gray-700 hover:bg-gray-50">Pay Now →</button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerAddresses() {
  const { user, updateProfile } = useCustomerAuth();
  const [form, setForm] = useState({
    name: user?.name || '', phone: user?.phone || '', address_line1: user?.address || '', city: user?.city || 'Lahore',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    await updateProfile(form);
    toast.success('✅ Address updated! Next delivery uses this address.');
  };
  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Delivery</div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">My Addresses</h1>
        <p className="text-sm text-gray-500 mt-1">Update your delivery address. Changes apply automatically to upcoming issues.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl border-2 p-7 relative bg-white shadow-sm" style={{ borderColor: FLARE_RED }}>
          <span className="absolute top-5 right-5 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: FLARE_RED, color: '#fff' }}>DEFAULT</span>
          <h3 className="text-sm font-black uppercase tracking-widest text-gray-500 mb-4">🏠 Home</h3>
          <div className="font-bold text-gray-900 text-lg mb-1">{form.name || user?.name}</div>
          <div className="text-gray-600">{form.address_line1 || 'No address saved yet'}</div>
          <div className="text-gray-600">{form.city}, Pakistan</div>
          <div className="text-gray-500 mt-2">📞 {form.phone || 'Not set'}</div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-3xl p-7 border border-gray-100 shadow-sm space-y-5">
          <h3 className="text-sm font-black uppercase tracking-widest text-gray-500 mb-1">✏️ Edit Address</h3>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Full Name (on package)</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent" style={{ '--tw-ring-color': FLARE_RED }} />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Phone</label>
            <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Street Address</label>
            <textarea rows="3" value={form.address_line1} onChange={e => set('address_line1', e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">City</label>
            <input value={form.city} onChange={e => set('city', e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2" />
          </div>
          <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-white font-bold shadow-md hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: FLARE_RED }}>💾 Save Address</button>
        </form>
      </div>
    </div>
  );
}

function CustomerPreferences() {
  const { user, updateProfile } = useCustomerAuth();
  const [form, setForm] = useState({
    newsletter_optin: !!user?.newsletter_optin,
    whatsapp_updates: !!user?.whatsapp_updates,
    delivery_updates: true,
    offer_alerts: true,
    event_invites: false,
  });
  const toggle = k => setForm(p => ({ ...p, [k]: !p[k] }));
  const save = async () => {
    await updateProfile(form);
    toast.success('✅ Notification preferences saved!');
  };
  const rows = [
    { k: 'newsletter_optin', title: 'Flash Newsletter', desc: 'Monthly issue alerts, sneak peeks & subscriber-only announcements (email).' },
    { k: 'whatsapp_updates', title: 'WhatsApp Updates', desc: 'Delivery tracking photos & short status updates over WhatsApp.' },
    { k: 'delivery_updates', title: 'Delivery Emails', desc: 'Shipping confirmations + tracking links via email every month.' },
    { k: 'offer_alerts', title: 'Exclusive Offers', desc: 'Flash sales, 2-for-1 issues, seasonal discounts.' },
    { k: 'event_invites', title: 'Event Invites', desc: 'Annual subscriber dinner, meet-the-editor, launch parties.' },
  ];
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Notifications</div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">My Preferences</h1>
        <p className="text-sm text-gray-500 mt-1">Choose exactly what Flare sends you. Unsubscribe anytime.</p>
      </div>
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm divide-y divide-gray-50">
        {rows.map(r => (
          <div key={r.k} className="flex items-start gap-4 p-5 sm:p-6">
            <div className="flex-1 min-w-0">
              <div className="font-bold text-gray-900">{r.title}</div>
              <div className="text-sm text-gray-500 mt-0.5">{r.desc}</div>
            </div>
            <button
              onClick={() => toggle(r.k)}
              className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0"
              style={{ backgroundColor: form[r.k] ? FLARE_RED : '#d1d5db' }}
            >
              <span className={'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' + (form[r.k] ? 'translate-x-6' : 'translate-x-1')} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button onClick={save} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold shadow-md hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: FLARE_RED }}>💾 Save Preferences</button>
      </div>
    </div>
  );
}

function CustomerProfile() {
  const { user, updateProfile, refresh } = useCustomerAuth();
  const [form, setForm] = useState({
    name: user?.name || '', email: user?.email || '', phone: user?.phone || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    await updateProfile(form);
    refresh();
    toast.success('✅ Profile updated.');
  };
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Account</div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">My Profile</h1>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="rounded-3xl border border-gray-100 shadow-sm p-6 text-center bg-white">
          <div className="mx-auto mb-4 w-24 h-24 rounded-3xl flex items-center justify-center text-white text-4xl font-black shadow-sm" style={{ backgroundColor: FLARE_RED }}>
            {String(user?.name || 'C').trim().charAt(0).toUpperCase()}
          </div>
          <div className="font-black text-gray-900 text-xl">{user?.name}</div>
          <div className="text-sm text-gray-500 truncate">{user?.email}</div>
          <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider" style={{ backgroundColor: '#ecfdf5', color: '#059669' }}>⭐ Verified Member</div>
          <div className="mt-6 grid grid-cols-2 gap-3 text-left">
            <div className="rounded-xl p-3 bg-gray-50"><div className="text-[10px] font-black uppercase tracking-widest text-gray-500">Joined</div><div className="text-xs font-bold text-gray-800">{fmtDate(user?.created_at)}</div></div>
            <div className="rounded-xl p-3 bg-gray-50"><div className="text-[10px] font-black uppercase tracking-widest text-gray-500">User ID</div><div className="text-xs font-bold text-gray-800 break-words">{String(user?.id || '').slice(0, 12)}</div></div>
          </div>
        </div>
        <form onSubmit={submit} className="md:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8 space-y-5">
          <h3 className="text-sm font-black uppercase tracking-widest text-gray-500">Personal Information</h3>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Full Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Email *</label>
            <input type="email" value={form.email} disabled className="w-full px-4 py-3 rounded-xl border border-gray-100 bg-gray-50 text-gray-500 cursor-not-allowed" />
            <p className="text-xs text-gray-400 mt-1">Contact support to change your email address.</p>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Phone / WhatsApp</label>
            <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2" />
          </div>
          <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-white font-bold shadow-md hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: FLARE_RED }}>💾 Save Changes</button>
        </form>
      </div>
    </div>
  );
}

function CustomerSecurity() {
  const { changePassword } = useCustomerAuth();
  const [form, setForm] = useState({ old: '', next: '', confirm: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { toast.error('New passwords do not match'); return; }
    try {
      await changePassword(form.old, form.next);
      toast.success('✅ Password changed successfully!');
      setForm({ old: '', next: '', confirm: '' });
    } catch (err) { toast.error(err.message || 'Change failed'); }
  };
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Safety</div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Security</h1>
      </div>
      <form onSubmit={submit} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8 space-y-5">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1.5">Current Password</label>
          <input type="password" value={form.old} onChange={e => set('old', e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2" />
        </div>
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1.5">New Password (min 6)</label>
          <input type="password" minLength={6} value={form.next} onChange={e => set('next', e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2" />
        </div>
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1.5">Confirm New Password</label>
          <input type="password" minLength={6} value={form.confirm} onChange={e => set('confirm', e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2" />
        </div>
        <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-white font-bold shadow-md" style={{ backgroundColor: FLARE_RED }}>🔑 Update Password</button>
      </form>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8">
        <h3 className="text-lg font-black text-gray-900 tracking-tight mb-1">Active Sessions</h3>
        <p className="text-sm text-gray-500 mb-5">You're signed in on 1 device.</p>
        <div className="rounded-2xl border border-gray-100 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-600">🖥</div>
            <div>
              <div className="font-bold text-sm text-gray-900">This Device · Current Session</div>
              <div className="text-xs text-gray-500">Just now · {window.navigator?.userAgent?.slice(0, 60)}…</div>
            </div>
          </div>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider" style={{ backgroundColor: '#ecfdf5', color: '#059669' }}>ACTIVE</span>
        </div>
      </div>
    </div>
  );
}

export {
  ProtectedCustomerRoute,
  CustomerAccountLayout,
  CustomerDashboard,
  CustomerSubscriptions,
  CustomerOrders,
  CustomerInvoices,
  CustomerAddresses,
  CustomerPreferences,
  CustomerProfile,
  CustomerSecurity,
  CustomerSupport,
};
export default CustomerAccountLayout;

function CustomerSupport() {
  const { user } = useCustomerAuth();
  const brand = useSiteBrand();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    subject: '',
    reason: 'Subscription Issue',
    message: '',
    source: 'Flare Customer',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e && e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setErrors({});
    try {
      const res = await contactApi.submitRequest(form);
      setDone(res);
      toast.success('✅ Your support request was sent to the Flare team!');
      setForm(f => ({ ...f, subject: '', message: '' }));
    } catch (err) {
      const list = err?.details || [err?.message || 'Could not send your message. Please try again.'];
      const map = {};
      if (/name/i.test(list[0])) map.name = list[0];
      if (/email/i.test(list[0])) map.email = list[0];
      if (/subject/i.test(list[0])) map.subject = list[0];
      if (/message/i.test(list[0])) map.message = list[0];
      setErrors(map);
      toast.error(list[0]);
    } finally {
      setSubmitting(false);
    }
  };

  const contactCards = [
    { title: 'Email Us', sub: 'Reply within 4–12 hours', value: brand.email, href: brand.mailHref, color: 'from-indigo-500 to-blue-600', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { title: 'Call / WhatsApp', sub: 'Mon–Sun · 10am–9pm', value: brand.phone, href: brand.waHref || brand.telHref, color: 'from-emerald-500 to-green-600', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' },
    { title: 'Office Visits', sub: 'By appointment only', value: 'Gulberg III · Lahore', href: 'https://maps.google.com/?q=Gulberg+III+Lahore+Pakistan', color: 'from-amber-500 to-orange-600', icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z' },
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest mb-3 border" style={{ color: FLARE_RED, borderColor: 'rgba(196,21,28,0.25)', backgroundColor: 'rgba(196,21,28,0.06)' }}>
            <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" className="h-3.5 w-3.5" />
            Flare Customer · Ticket Channel
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-gray-900 mb-2">Contact Support</h1>
          <p className="text-gray-500 font-medium leading-relaxed max-w-2xl">
            We're here for our subscribers. Send us a message — the team replies within a few hours. Your request ID and status are emailed automatically.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Team Online
        </div>
      </div>

      {done?.ok && (
        <div className="p-5 rounded-2xl bg-emerald-50 border-2 border-emerald-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow">
              <Icon path="M5 13l4 4L19 7" className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-black text-emerald-900 text-lg mb-0.5">Request Received! 🎉</div>
              <div className="text-sm text-emerald-800 font-semibold">
                Reference ID: <span className="font-mono bg-white px-2 py-0.5 rounded-lg border border-emerald-200">{done.record.id}</span>
                {done.stored === 'local' && done.note && (
                  <span className="block mt-1.5 text-emerald-700/90 text-xs">💡 {done.note}</span>
                )}
              </div>
              <div className="text-sm text-emerald-700 mt-1.5">
                Our team will reply to <b>{done.record.email}</b> shortly.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
        {contactCards.map(c => (
          <a key={c.title} href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined} className="group bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-gradient-to-br ${c.color} shadow-md`}>
              <Icon path={c.icon} className="h-6 w-6 text-white" />
            </div>
            <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">{c.title}</div>
            <div className="font-black text-gray-900 text-lg leading-tight mb-0.5">{c.value}</div>
            <div className="text-xs font-semibold text-gray-500">{c.sub}</div>
          </a>
        ))}
      </div>

      <form onSubmit={submit} className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900 mb-1">Send us a message</h2>
            <p className="text-gray-500 font-medium">Fill in the details below. Your account information is auto-filled for faster help.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-black text-gray-800 mb-2">Full Name <span className="text-red-500">*</span></label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Ahmed Khan" className={`w-full px-4 py-3.5 rounded-xl border-2 ${errors.name ? 'border-red-400 bg-red-50/40' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white'} outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400`} required />
            {errors.name && <p className="text-sm text-red-600 font-bold mt-1.5">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-sm font-black text-gray-800 mb-2">Email Address <span className="text-red-500">*</span></label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@example.com" className={`w-full px-4 py-3.5 rounded-xl border-2 ${errors.email ? 'border-red-400 bg-red-50/40' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white'} outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400`} required />
            {errors.email && <p className="text-sm text-red-600 font-bold mt-1.5">{errors.email}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-black text-gray-800 mb-2">Phone / WhatsApp <span className="text-gray-400 font-bold">(optional)</span></label>
            <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+92 300 1234567" className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400" />
          </div>
          <div>
            <label className="block text-sm font-black text-gray-800 mb-2">Reason</label>
            <div className="relative">
              <select value={form.reason} onChange={e => set('reason', e.target.value)} className="w-full px-4 pr-10 py-3.5 rounded-xl border-2 border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white outline-none transition-colors font-medium text-gray-900 appearance-none cursor-pointer">
                {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                <Icon path="M19 9l-7 7-7-7" className="h-4 w-4" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-black text-gray-800 mb-2">Subject Line <span className="text-red-500">*</span></label>
          <input type="text" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Briefly describe the issue or request" className={`w-full px-4 py-3.5 rounded-xl border-2 ${errors.subject ? 'border-red-400 bg-red-50/40' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white'} outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400`} required />
          {errors.subject && <p className="text-sm text-red-600 font-bold mt-1.5">{errors.subject}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-black text-gray-800">Your Message <span className="text-red-500">*</span></label>
            <span className={`text-[11px] font-black uppercase tracking-widest ${form.message.length >= 500 ? 'text-red-600' : 'text-gray-400'}`}>{form.message.length} / 2000</span>
          </div>
          <textarea value={form.message} onChange={e => set('message', e.target.value.slice(0, 2000))} placeholder="Tell us what we can help you with… subscription ID, order number (FLR-XXXX), delivery address — the more detail the faster we can respond!" rows={7} className={`w-full px-4 py-3.5 rounded-xl border-2 resize-y min-h-[180px] ${errors.message ? 'border-red-400 bg-red-50/40' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white'} outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400 leading-relaxed`} required />
          {errors.message && <p className="text-sm text-red-600 font-bold mt-1.5">{errors.message}</p>}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
          <div className="text-xs font-bold text-gray-500 leading-relaxed max-w-md">🔒 Your info is safe. Response times: weekdays 2–4h · weekends 4–12h. Account context auto-attached.</div>
          <button type="submit" disabled={submitting} className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl text-white font-black shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed min-w-[220px]" style={{ backgroundColor: FLARE_RED, boxShadow: '0 12px 30px -12px rgba(196,21,28,0.5)' }}>
            {submitting ? (
              <>
                <span className="w-4 h-4 rounded-full border-[3px] border-white/40" style={{ borderTopColor: '#fff', animation: 'flare-spin 0.8s linear infinite' }} />
                Sending Request…
              </>
            ) : (
              <>
                <Icon path="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" className="h-5 w-5" />
                Send to Flare Team
              </>
            )}
          </button>
        </div>
        <style>{`@keyframes flare-spin { to { transform: rotate(360deg); } }`}</style>
      </form>
    </div>
  );
}
